import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Upload, FileText, AlertCircle, Loader2 } from "lucide-react";
import logoImage from "@/assets/greenvision-logo.png";

type DocumentType = {
  key: string;
  label: string;
};

type LookupResult = {
  ok: boolean;
  submissionId: string;
  locationName: string;
  missingDocs: DocumentType[];
  allDocumentTypes: DocumentType[];
};

export default function ExternalDocumentUpload() {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [lookupData, setLookupData] = useState<LookupResult | null>(null);
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Geen upload token gevonden");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const { data, error: invokeError } = await supabase.functions.invoke('intake-upload-lookup', {
          body: { token },
        });

        if (invokeError) throw invokeError;

        if (!data.ok) {
          setError(data.error || "Token ongeldig");
          setErrorCode(data.code || null);
          return;
        }

        setLookupData(data);
      } catch (e: any) {
        console.error("[ExternalDocumentUpload] lookup failed", e);
        setError(e.message || "Er ging iets mis");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleFileChange = (key: string, file: File | null) => {
    setFiles(prev => ({ ...prev, [key]: file }));
  };

  const selectedFilesCount = Object.values(files).filter(f => f !== null).length;

  const handleSubmit = async () => {
    if (!token || selectedFilesCount === 0) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('token', token);

      for (const [key, file] of Object.entries(files)) {
        if (file) {
          formData.append(key, file);
        }
      }

      const { data, error: invokeError } = await supabase.functions.invoke('intake-upload-external', {
        body: formData,
      });

      if (invokeError) throw invokeError;

      if (!data.ok) {
        throw new Error(data.error || "Upload mislukt");
      }

      setSubmitted(true);
    } catch (e: any) {
      console.error("[ExternalDocumentUpload] submit failed", e);
      setError(e.message || "Upload mislukt");
    } finally {
      setSubmitting(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#014336] to-[#016654]">
        <header className="px-6 py-4">
          <img src={logoImage} alt="GreenVision" className="h-10" />
        </header>

        <div className="flex items-center justify-center px-4 py-12">
          <Card className="w-full max-w-lg text-center">
            <CardHeader>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Documenten verzonden!</CardTitle>
              <CardDescription>
                Je documenten zijn succesvol geüpload. Bedankt voor het aanvullen van je intake.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Je kunt dit venster nu sluiten.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#014336] to-[#016654]">
        <header className="px-6 py-4">
          <img src={logoImage} alt="GreenVision" className="h-10" />
        </header>

        <div className="flex items-center justify-center px-4 py-12">
          <Card className="w-full max-w-lg text-center">
            <CardContent className="py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
              <p className="mt-4 text-muted-foreground">Laden...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    const getErrorMessage = () => {
      switch (errorCode) {
        case 'INVALID_TOKEN':
          return 'Deze uploadlink is ongeldig of niet gevonden.';
        case 'TOKEN_USED':
          return 'Deze uploadlink is al gebruikt. Je hebt al documenten geüpload.';
        case 'TOKEN_EXPIRED':
          return 'Deze uploadlink is verlopen. Neem contact op met GreenVision voor een nieuwe link.';
        default:
          return error;
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-b from-[#014336] to-[#016654]">
        <header className="px-6 py-4">
          <img src={logoImage} alt="GreenVision" className="h-10" />
        </header>

        <div className="flex items-center justify-center px-4 py-12">
          <Card className="w-full max-w-lg text-center">
            <CardHeader>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl">Fout</CardTitle>
              <CardDescription>{getErrorMessage()}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  // Main upload form
  if (!lookupData) return null;

  const { locationName, missingDocs } = lookupData;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#014336] to-[#016654]">
      <header className="px-6 py-4">
        <img src={logoImage} alt="GreenVision" className="h-10" />
      </header>

      <div className="px-4 py-8">
        <Card className="mx-auto w-full max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Documenten Uploaden
            </CardTitle>
            <CardDescription>
              Upload de ontbrekende documenten voor je EMS intake bij <strong>{locationName}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {missingDocs.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 mx-auto text-green-600 mb-4" />
                <p className="text-lg font-medium">Alle documenten zijn al geüpload!</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Er zijn geen ontbrekende documenten meer voor deze intake.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {missingDocs.length} document(en) ontbrekend
                  </Badge>
                </div>

                <div className="space-y-4">
                  {missingDocs.map((doc) => (
                    <div
                      key={doc.key}
                      className="rounded-lg border border-dashed border-muted-foreground/25 p-4"
                    >
                      <Label htmlFor={doc.key} className="text-sm font-medium">
                        {doc.label}
                      </Label>
                      <Input
                        id={doc.key}
                        type="file"
                        className="mt-2"
                        onChange={(e) => handleFileChange(doc.key, e.target.files?.[0] || null)}
                        accept=".pdf,.xlsx,.xls,.csv,.doc,.docx"
                      />
                      {files[doc.key] && (
                        <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          {files[doc.key]?.name}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t">
                  <Button
                    onClick={handleSubmit}
                    disabled={selectedFilesCount === 0 || submitting}
                    className="w-full bg-[#014336] hover:bg-[#016654]"
                    size="lg"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploaden...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {selectedFilesCount > 0
                          ? `${selectedFilesCount} document(en) verzenden`
                          : "Selecteer bestanden om te uploaden"}
                      </>
                    )}
                  </Button>
                  <p className="mt-3 text-xs text-center text-muted-foreground">
                    Ondersteunde bestandstypen: PDF, Excel, Word, CSV
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
